import { Controller } from "@hotwired/stimulus"
import { ICON_BG, ICON_COLORS, applyIconTileStyle, FREQ_BADGE_CLASS, categoryLabel, iconSvgHtml } from "../helpers/expense_constants"
import { intlLocale, speechLocale, timerUnit } from "../helpers/locale"

const TRANSCRIPT_MAX_CHARS = 200
const RECORDING_DURATION_SECS = 15
// Bumped to v2 when receipt scanning shipped. The v1 screen listed only the
// text a user typed or dictated, so an acceptance recorded against it is not
// permission to send a photograph of their receipt to OpenAI. Everyone sees the
// current wording once more rather than a photo leaving a device on the
// strength of a promise that never covered it.
const AI_CONSENT_STORAGE_KEY = "floosyo:ai-consent:v2"

// The card resizes on the sheet's own entry curve, a touch quicker than the
// 460ms slide-in: this is a settle, not an arrival.
const CARD_RESIZE_MS = 320
const CARD_RESIZE_EASING = "cubic-bezier(0.2, 0.8, 0.25, 1)"

export default class extends Controller {
  static targets = ["sheet", "backdrop", "micButton", "micIcon", "status", "transcript",
                    "submitButton", "inputArea", "loadingArea", "confirmCard", "consentArea",
                    "mascotOff", "mascotOn", "mascotConfused", "rippleRings",
                    "displayArea", "editForm", "editName", "editAmount", "editCurrency",
                    "frequencyPill", "freqSlider", "freqTrack",
                    "recurringToggle", "recurringKnob",
                    "categoryTrigger", "categoryTriggerIcon", "categoryTriggerLabel",
                    "categoryPickerOverlay", "categoryGridItem",
                    "editButtons", "manualButtons", "manualError", "manualErrorText",
                    "dictationAlert", "dictationAlertText", "retryScan",
                    "fab", "bottomNav", "dock", "card",
                    "editStartDate", "editStartDateDisplay", "startDateSection", "renewalPreview", "renewalPreviewWrapper",
                    "charCount", "timerText",
                    "confirmButton", "applyEditButton", "manualSaveButton",
                    "headerSave", "clearButton", "noBudgetToast",
                    ]

  static values = {
    minChars:        { type: Number, default: 1 },
    parseUrl:        String,
    saveUrl:         String,
    newUrl:          String,
    i18n:            Object,
    defaultCurrency: { type: String, default: "JOD" }
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  // Dock visibility is derived from these four, never set directly. _syncDock
  // is the only thing that reads them and the only thing that moves the dock.
  initialize() {
    this._sheetOpen = false
    this._inputVisible = true
    this._sheetDragging = false
    this._typing = false
    this._dockShown = null
    this._cardResizePending = false
    // The photo behind an in-flight or failed scan, held only for a retry.
    this._scanBlob = null
  }

  connect() {
    console.log("[Voice] modal connected, bridge available:", !!this._bridgeController)

    // First open() pays a hidden cost: WebKit must lay out and paint the
    // entire sheet subtree (1000+ nodes) plus decode the 3 mascot SVGs
    // (~290KB total) the first time the sheet animates in. That work
    // collides with the 460ms transform and the simultaneous native
    // bridge.start() audio-engine cold-start, producing the first-tap jank
    // users see. We pre-warm both layout and image decoding at idle so the
    // first real open() finds them already in WebKit's cache.
    this._scheduleFirstOpenWarmup()
  }

  disconnect() {
    if (this._warmupHandle) {
      const cancel = window.cancelIdleCallback || clearTimeout
      cancel(this._warmupHandle)
      this._warmupHandle = null
    }
    // Torn down with the sheet still open — a Turbo visit while it was up, say.
    // close() never ran, and a page left at overflow:hidden cannot be scrolled
    // by anything short of a reload, so the lock is released here too.
    if (this._sheetOpen) document.body.style.overflow = ""
    this._stopListening()
  }

  _scheduleFirstOpenWarmup() {
    const run = () => {
      this._warmupHandle = null

      // Force decode of mascot SVGs so the first time they're shown the
      // main thread doesn't stall decoding ~290KB of XML.
      const mascots = [
        this.hasMascotOffTarget ? this.mascotOffTarget : null,
        this.hasMascotOnTarget ? this.mascotOnTarget : null,
        this.hasMascotConfusedTarget ? this.mascotConfusedTarget : null,
      ].filter(Boolean)
      for (const img of mascots) {
        if (typeof img.decode === "function") {
          img.decode().catch(() => {})
        }
      }

      // Reading offsetHeight forces synchronous layout of the (off-screen,
      // translated) sheet subtree. Subsequent reads/animations reuse the
      // cached layout.
      if (this.hasSheetTarget) void this.sheetTarget.offsetHeight
    }

    if (typeof window.requestIdleCallback === "function") {
      this._warmupHandle = window.requestIdleCallback(run, { timeout: 1500 })
    } else {
      this._warmupHandle = setTimeout(run, 400)
    }
  }

  saveUrlValueChanged() {
    if (!this.hasBottomNavTarget) return
    this.bottomNavTarget.classList.toggle("hidden", !this.saveUrlValue)
  }

  // ── Modal open / close ─────────────────────────────────────────────────────

  open() {
    // No budget means the mic has nowhere to save an expense. Say so rather
    // than swallowing the tap — the account has deliberately zero budgets and
    // the dashboard is showing the add-budget cards right behind this toast.
    if (!this.saveUrlValue) {
      this._showNoBudgetToast()
      return
    }

    this._resetSheet()
    this._sheetOpen = true
    this._sheetDragging = false

    // Remove hidden added by _handleSaveSuccess force-close
    this.sheetTarget.classList.remove("hidden")
    this.backdropTarget.classList.remove("hidden")

    // Backdrop — fade in (opacity transition handled by CSS)
    this.backdropTarget.classList.remove("opacity-0", "pointer-events-none")

    // Lock the page underneath so it can't scroll while the sheet is up.
    // The same line the filter sheet has always had. This sheet leaned on its
    // drag handler for it instead: every touch on the sheet started a drag, and
    // a drag preventDefault()s, so the dashboard happened to stay put. Once the
    // drag was narrowed to [data-drag-handle], every other touch fell straight
    // through to the page — and the consent overlay, which has no handle at
    // all, scrolled the dashboard from top to bottom.
    document.body.style.overflow = "hidden"

    // Sheet — iOS spring (overshoot bounce, smooth settle)
    this.sheetTarget.style.transition = "transform 460ms cubic-bezier(0.2, 0.8, 0.25, 1)"
    this.sheetTarget.offsetHeight // force reflow so transition fires from current position
    this.sheetTarget.style.transform = "translateY(0)"

    // Hide the native bottom nav synchronously — it's a single bridge send
    // and the visual change should happen alongside the sheet entry.
    this._bottomNavBridge?.hide()

    // Defer everything that competes with first-frame paint:
    //  • dim() bridge call — adds a native overlay view; not visually
    //    critical and creates a second bridge round-trip.
    //  • _beginRecording() — kicks off the speech-recognition bridge.
    //    On first-ever recording, AVAudioSession.setActive cold-starts in
    //    100–300 ms on the @MainActor, which blocks the WKWebView main
    //    thread. Running it after the sheet has had a chance to paint its
    //    first frame keeps the entry animation smooth.
    //
    // We use rAF×2 to land AFTER the browser has committed the first
    // frame of the slide-in (post style flush + paint), not just before.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._bottomNavBridge?.dim()

        // Deferred to here so the native dock's spring runs alongside the
        // sheet settling instead of arriving before it has moved.
        this._syncDock()

        // First-run AI consent gate. Required by App Review Guideline
        // 5.1.2(i): explicit permission before sharing data with third-
        // party AI. We name OpenAI in the consent copy and only proceed
        // once the user accepts.
        if (!this._hasAiConsent()) {
          this._showConsentOverlay()
          return
        }

        this._beginRecording()
      })
    })
  }

  acceptAiConsent() {
    try {
      localStorage.setItem(AI_CONSENT_STORAGE_KEY, "1")
    } catch (_e) {
      // Private browsing / storage disabled — proceed for this session only.
    }
    this._hideConsentOverlay()
    this._beginRecording()
  }

  cancelAiConsent() {
    this._hideConsentOverlay()
    this.close()
  }

  _hasAiConsent() {
    try {
      return localStorage.getItem(AI_CONSENT_STORAGE_KEY) === "1"
    } catch (_e) {
      return false
    }
  }

  _showConsentOverlay() {
    if (!this.hasConsentAreaTarget) return
    this.consentAreaTarget.classList.remove("hidden")
    if (this.hasInputAreaTarget) this.inputAreaTarget.classList.add("hidden")
    this._setInputVisible(false)
    this._syncHeaderSave()
  }

  _hideConsentOverlay() {
    if (this.hasConsentAreaTarget) this.consentAreaTarget.classList.add("hidden")
    if (this.hasInputAreaTarget) this.inputAreaTarget.classList.remove("hidden")
    this._setInputVisible(true)
    this._syncHeaderSave()
  }

  _beginRecording() {
    // The sheet reopens on whichever way in was used last. Opening onto Scan or
    // Type must not switch the microphone on behind a panel that never asked
    // for it — that is the tab's own mascot's job.
    if (!this._onSpeakTab) return

    this._discardPendingResult = false
    this._hideDictationAlert()
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this._t("starting")
    }

    // Try bridge first (native iOS). If controller isn't connected, fall back to Web Speech API.
    const bridge = this._bridgeController
    if (bridge) {
      console.log("[Voice] bridge connected — using native recognition")
      bridge._modalController = this
      bridge.start()
      this._nativeRecording = true
    } else {
      console.log("[Voice] no bridge — using Web Speech API fallback")
      this._startListening()
    }
  }

  close() {
    // pointer-events-none MUST be added synchronously — if a Turbo morph/refresh
    // happens during the animation, the old backdrop could persist and block
    // all touches on iOS. (Copied from confirm-sheet pattern.)
    this.backdropTarget.style.opacity = ""  // clear any inline opacity from drag
    this.backdropTarget.classList.add("opacity-0", "pointer-events-none")

    // Restore page scrolling. Every way out of the sheet lands here — the close
    // button, the backdrop, a committed drag, save, and _closeAndNavigate — so
    // this one line covers all of them.
    document.body.style.overflow = ""

    this._sheetOpen = false
    this._syncDock()

    // Sheet — iOS dismiss (smooth deceleration)
    this.sheetTarget.style.transition = "transform 340ms cubic-bezier(0.4, 0, 0.2, 1)"
    // Same resting place the markup starts from. 100% left the web sheet,
    // which floats 24px off the bottom, peeking over the page edge.
    this.sheetTarget.style.transform = "translateY(115%)"


    // Remove dimming and show native bottom nav bar again
    this._bottomNavBridge?.undim()
    this._bottomNavBridge?.show()

    if (this._nativeRecording) {
      this._bridgeController?.stop()
      this._nativeRecording = false
    }
    this._stopListening()
  }

  // ── Backdrop tap to close ──────────────────────────────────────────────────

  backdropClose(event) {
    if (event.target === this.backdropTarget) this.close()
  }

  // ── Swipe-to-dismiss (iOS-style drag from anywhere on the sheet) ─────────

  // Selector for elements that should consume the touch instead of starting a drag
  // (textarea/input/button/select/links + anything explicitly opted-in via data-no-drag).
  static DRAG_IGNORE_SELECTOR = "input, textarea, select, button, a, label, [contenteditable], [data-no-drag]"

  // A sheet is dragged by its handle, not by its contents. Binding the whole
  // surface meant a touch anywhere — on a mascot, on a paragraph, on the padding
  // between two rows — slid the sheet and moved the dashboard behind it, which
  // reads as the page coming loose rather than as a gesture.
  static DRAG_HANDLE_SELECTOR = "[data-drag-handle]"

  dragStart(event) {
    // Ignore touches that originate on interactive controls so taps/typing still work.
    if (event.target.closest(this.constructor.DRAG_IGNORE_SELECTOR)) {
      this._isDragging = false
      return
    }
    if (!event.target.closest(this.constructor.DRAG_HANDLE_SELECTOR)) {
      this._isDragging = false
      return
    }
    const touch = event.touches[0]
    this._dragStartY = touch.clientY
    this._dragCurrentY = touch.clientY
    this._dragStartX = touch.clientX
    this._dragCommitted = false   // becomes true once the gesture is clearly a vertical drag
    this._isDragging = true
    this.sheetTarget.style.transition = "none"
  }

  dragMove(event) {
    if (!this._isDragging) return

    const touch = event.touches[0]
    this._dragCurrentY = touch.clientY
    const deltaY = this._dragCurrentY - this._dragStartY
    const deltaX = touch.clientX - this._dragStartX

    // Decide direction once a small threshold is crossed. Horizontal-dominant
    // movement releases the gesture so things like text selection still work.
    if (!this._dragCommitted) {
      if (Math.abs(deltaY) < 6 && Math.abs(deltaX) < 6) return
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        this._isDragging = false
        return
      }
      this._dragCommitted = true
    }

    if (deltaY <= 0) {
      this.sheetTarget.style.transform = "translateY(0)"
      // An upward drag is clamped to rest: the card has not moved, so the dock
      // has no reason to be anywhere but under it.
      this._setSheetDragging(false)
      return
    }

    // Prevent the page underneath from scrolling while we drag the sheet down.
    if (event.cancelable) event.preventDefault()

    // The web dock rides the sheet; the native one is pinned to the window and
    // would sit still while the card slid out from under it. Following the
    // finger would cost a bridge message per frame, so it steps out instead —
    // but only now, once the sheet is genuinely translating.
    this._setSheetDragging(true)

    this.sheetTarget.style.transform = `translateY(${deltaY}px)`
    const progress = Math.min(deltaY / (window.innerHeight * 0.3), 1)
    this.backdropTarget.style.opacity = 1 - progress * 0.8
  }

  dragEnd() {
    if (!this._isDragging) return
    const wasCommitted = this._dragCommitted
    this._isDragging = false
    this._dragCommitted = false

    if (!wasCommitted) {
      // Gesture never crossed the commit threshold (it was a tap or horizontal swipe).
      this.sheetTarget.style.transition = ""
      this.sheetTarget.style.transform = ""
      this.backdropTarget.style.opacity = ""
      this._setSheetDragging(false)
      return
    }

    const deltaY = this._dragCurrentY - this._dragStartY
    const velocity = deltaY / (window.innerHeight || 1)

    if (deltaY > 100 || velocity > 0.2) {
      this.close()
    } else {
      this.sheetTarget.style.transition = "transform 300ms cubic-bezier(0.2, 0.8, 0.25, 1)"
      this.sheetTarget.style.transform = "translateY(0)"
      this.backdropTarget.style.opacity = ""
      // Snapped back. A dismiss deliberately does not land here: close() takes
      // the dock down with the sheet rather than springing it back first.
      this._setSheetDragging(false)
    }
  }

  // ── Mic button tap ─────────────────────────────────────────────────────────

  toggleListening() {
    const bridge = this._bridgeController
    if (bridge) {
      if (this._nativeRecording) {
        bridge.stop()
        this._nativeRecording = false
        this._setRecordingState(false)
        if (this.hasStatusTarget) {
          this.statusTarget.textContent = this._t("tap_to_start")
        }
      } else {
        bridge._modalController = this
        this._discardPendingResult = false
        this._hideDictationAlert()
        this._setRecordingState(true)
        if (this.hasStatusTarget) {
          this.statusTarget.textContent = this._t("starting")
        }
        bridge.start()
        this._nativeRecording = true
      }
    } else {
      this._isListening ? this._stopListening() : this._startListening()
    }
  }

  // ── Manual input ──────────────────────────────────────────────────────────

  onTranscriptInput() {
    this._updateSubmitState()
  }

  // ── Clear transcript (header pill) ─────────────────────────────────────────

  // Wipes the transcript field and any committed native-recording segments so
  // the user can start fresh. Stops an in-flight recording first — otherwise
  // the next partial result would immediately repopulate the field.

  // ── Ways-in deck ──────────────────────────────────────────────────────────

  // Index 1 is Speak. The deck reflects its current tab onto the input area as
  // a Stimulus value attribute, which is the cheapest thing to read here.
  get _onSpeakTab() {
    if (!this.hasSheetTarget) return true
    const index = this.sheetTarget.dataset.expenseDeckIndexValue
    return Number(index) === 1
  }

  // Scan and Type have no microphone on screen, so nothing may be listening
  // while either is showing.
  onDeckTabChange(event) {
    if (Number(event.detail?.index) === 1) return
    if (this._nativeRecording) {
      this._discardPendingResult = true
      this._bridgeController?.stop()
      this._nativeRecording = false
      this._setRecordingState(false)
    }
    if (this._isListening) this._stopListening()

    // Stopping the recogniser swaps the mascot back but says nothing about the
    // status line, which keeps whatever it last read. Coming back to Speak on a
    // mascot that is plainly idle under the word "Listening" is the bug.
    this._resetSpeakStatus()
  }

  // The Speak panel at rest: no countdown, no elapsed time, and the invitation
  // to start rather than a report of something that is no longer happening.
  _resetSpeakStatus() {
    this._stopTimer()
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this._t("tap_to_start")
    }
  }

  clearTranscript() {
    // Recognition finishes asynchronously after stop(): the native bridge
    // still delivers a final "transcript" event (and possibly late partials),
    // which would repopulate the field we just wiped. Flag the in-flight
    // result so the handlers discard it.
    if (this._nativeRecording || this._isListening) {
      this._discardPendingResult = true
    }
    if (this._nativeRecording) {
      this._bridgeController?.stop()
      this._nativeRecording = false
      this._setRecordingState(false)
    }
    if (this._isListening) this._stopListening()

    this._committedTranscript = ""
    this._lastPartial = ""
    if (this.hasTranscriptTarget) this.transcriptTarget.value = ""
    this._updateSubmitState()

    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this._t("tap_to_start")
    }
  }

  // The header save is only meaningful while the input area is showing — hide
  // it during loading / confirm card / consent overlay. Whether it is *enabled*
  // is not decided here: it carries the submitButton target too, so
  // _updateSubmitState owns that and applies the same minimum-length rule it
  // has always applied.
  _syncHeaderSave() {
    if (!this.hasHeaderSaveTarget) return
    const inputVisible = this.hasInputAreaTarget &&
      !this.inputAreaTarget.classList.contains("hidden")
    this.headerSaveTarget.classList.toggle("hidden", !inputVisible)
  }

  // ── Confirm card ───────────────────────────────────────────────────────────

  showConfirmCard() {
    // Stop any active recording before processing
    if (this._nativeRecording) {
      const bridge = this._bridgeController
      if (bridge) bridge.stop()
      this._nativeRecording = false
      this._setRecordingState(false)
    }
    if (this._isListening) {
      this._stopListening()
    }

    // Capture transcript BEFORE any DOM changes (iOS WebKit can clear hidden inputs)
    const transcript = this.transcriptTarget.value.trim()

    this._hide(this.inputAreaTarget)
    this._show(this.loadingAreaTarget)
    this._syncHeaderSave()

    this._parse({ transcript }, transcript)
  }

  // ── Scan ───────────────────────────────────────────────────────────────────

  // Called by bridge--expense-scan once the native picker has handed back a
  // downsampled JPEG. A receipt takes the same road a sentence does: same
  // endpoint, same usage limiter, same confirm card.
  scanImage(imageDataUrl) {
    // A reply that arrives without a URL is a bug, not a user action — the
    // bridge falls back to the request's own payload when its encode fails, so
    // this is reachable. Say something rather than looking broken.
    if (!imageDataUrl) {
      console.error("[Voice] scan reply carried no image")
      this.scanFailed(this._t("save_failed"))
      return
    }

    // Straight to bytes. The bridge has no choice but to hand the photo over as
    // a data URL — base64 is all a bridge message can carry — but that is a
    // third more bytes than the JPEG inside it, and the webview → Rails hop is
    // the only one the user waits on, on whatever signal they happen to have.
    // Rails re-encodes it for OpenAI on a link between two datacentres, where
    // the third costs nothing.
    const blob = this._dataUrlToBlob(imageDataUrl)
    if (!blob) {
      console.error("[Voice] scan reply was not a readable data URL")
      this.scanFailed(this._t("save_failed"))
      return
    }

    // Kept so a failed parse can be sent again without asking for the photo a
    // second time. By the time the network drops the receipt is usually back in
    // a pocket, and "take it again" is the wrong thing to ask for a fault that
    // was never the user's.
    this._scanBlob = blob
    this._parse({ image: blob }, "")
  }

  // Called by bridge--expense-scan when the picker could not run at all —
  // permission refused, no camera, or the asset failed to load.
  scanFailed(message) {
    this._showInputError(message || this._t("save_failed"))
  }

  // The banner's Retry, offered only by the scan flow: a transcript is still
  // sitting in the field to send again, but a photo is gone unless it was kept.
  retryScan(event) {
    event?.stopPropagation()
    if (!this._scanBlob) return
    this._hideDictationAlert()
    this._parse({ image: this._scanBlob }, "")
  }

  // Data URL → Blob without fetch(). The CSP's connect-src is `self` plus
  // PostHog, so fetching a data: URL back would simply be blocked.
  _dataUrlToBlob(dataUrl) {
    const comma = dataUrl.indexOf(",")
    if (comma === -1) return null

    try {
      const binary = atob(dataUrl.slice(comma + 1))
      const bytes = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
      return new Blob([bytes], { type: "image/jpeg" })
    } catch (_e) {
      return null
    }
  }

  // ── Parse ──────────────────────────────────────────────────────────────────

  // The one request both ways in share. `echo` is what the confirm card quotes
  // back: the spoken words for a transcript, nothing at all for a photo.
  _parse(body, echo) {
    this._hide(this.inputAreaTarget)
    this._show(this.loadingAreaTarget)
    this._syncHeaderSave()

    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    const isImage = body.image instanceof Blob

    // A photo goes as a file part, a sentence as JSON. Multipart costs a couple
    // of hundred bytes of boundary and saves a third of the image; the browser
    // writes the boundary itself, which is why Content-Type is left unset here.
    let payload, headers
    if (isImage) {
      payload = new FormData()
      payload.append("image", body.image, "receipt.jpg")
      headers = { "X-CSRF-Token": csrf }
    } else {
      payload = JSON.stringify(body)
      headers = { "Content-Type": "application/json", "X-CSRF-Token": csrf }
    }

    fetch(this.parseUrlValue, { method: "POST", headers, body: payload })
      .then(res => {
        if (!res.ok) {
          // A 502 from the proxy or a 500 that got as far as the error page is
          // not JSON, and letting the parse of it throw would replace a
          // translated message with a SyntaxError.
          return res.json()
            .catch(() => ({}))
            .then(err => {
              const failure = new Error(err.error || this._t("save_failed"))
              // The server says when sending the same bytes again cannot help:
              // an unreadable photo reads the same the second time, and a spent
              // usage limit is not spent any less. Anything unmarked is worth
              // another go.
              failure.retryable = err.retryable !== false
              throw failure
            })
        }
        return res.json()
      })
      .then(data => {
        if (data.error) throw new Error(data.error)
        this._scanBlob = null   // parsed; there is nothing left to retry
        this._parsedExpense = data   // stored for the save step
        this._populateConfirmCard(data, echo)
        this._hide(this.loadingAreaTarget)
        this._show(this.confirmCardTarget)
        this._refreshSaveButtonsState()
      })
      .catch((err) => {
        console.error("[Voice] parse error:", err.message)
        // fetch rejects with a TypeError when the request never reached the
        // server at all — no signal, airplane mode, a refused connection.
        // "Failed to fetch" is not a sentence to show anyone.
        const message = err.name === "TypeError" ? this._t("network_error") : err.message

        // A photo that cannot be read is not worth keeping: holding it would
        // only let the user resend it from the next failure's banner.
        if (err.retryable === false) this._scanBlob = null

        this._hide(this.loadingAreaTarget)
        this._show(this.inputAreaTarget)
        this._showInputError(message || this._t("save_failed"), { retry: isImage && !!this._scanBlob })
        this._updateSubmitState()
      })
  }

  // The banner inside the input area. Its target is named for dictation
  // because that was its first use; it is simply the input area's error line,
  // and the scan flow has the same need for one.
  // `retry` decides whether the banner carries a Retry alongside the message.
  // Only a scan whose photo is still in hand can offer one.
  _showInputError(message, { retry = false } = {}) {
    this._showDictationAlert(message)
    if (this.hasRetryScanTarget) this.retryScanTarget.classList.toggle("hidden", !retry)
  }

  backToInput() {
    this._hide(this.confirmCardTarget)
    this._hide(this.loadingAreaTarget)
    this._show(this.inputAreaTarget)
    this._updateSubmitState()
  }

  // ── Manual entry ────────────────────────────────────────────────────────

  openManualForm() {
    // Stop any active recording
    if (this._nativeRecording) {
      this._bridgeController?.stop()
      this._nativeRecording = false
    }
    this._stopListening()

    // Navigate to the dedicated new expense page (opens as native iOS modal)
    if (this.hasNewUrlValue && this.newUrlValue) {
      this._closeAndNavigate(this.newUrlValue)
      return
    }

    // Fallback: inline form (for non-native contexts without newUrl)
    this._openInlineManualForm()
  }

  // Navigate to new expense page with AI-parsed data prefilled
  openPrefilled(data) {
    if (!this.hasNewUrlValue || !this.newUrlValue) return

    const url = new URL(this.newUrlValue, window.location.origin)
    const fields = ["name", "amount", "currency", "category", "icon", "frequency", "recurring", "expense_type", "start_date", "notify_before_days", "brand_icon"]
    for (const key of fields) {
      if (data[key] != null && data[key] !== "") {
        url.searchParams.set(key, String(data[key]))
      }
    }

    this._closeAndNavigate(url.toString())
  }

  // Close the voice sheet, wait for animation to finish, then navigate
  _closeAndNavigate(url) {
    this.close()
    setTimeout(() => {
      if (this.hasSheetTarget) this.sheetTarget.classList.add("hidden")
      if (this.hasBackdropTarget) this.backdropTarget.classList.add("hidden")
      window.Turbo.visit(url)
    }, 400)
  }

  _openInlineManualForm() {
    this._isManualEntry = true

    // Create default expense data
    const now = new Date()
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    this._parsedExpense = {
      name: "",
      amount: "",
      currency: this.defaultCurrencyValue,
      category: "other",
      icon: "other",
      frequency: "monthly",
      recurring: true,
      expense_type: "consuming",
      start_date: today
    }

    this._populateEditForm(this._parsedExpense)

    // Clear amount for manual entry so user must type a value, then focus it
    this.editAmountTarget.value = ""
    setTimeout(() => this.editAmountTarget.focus(), 50)

    // Hide voice input, show edit form directly (skip display area)
    this._hide(this.inputAreaTarget)
    this._hide(this.loadingAreaTarget)
    this._show(this.confirmCardTarget)
    this._hide(this.displayAreaTarget)
    this._show(this.editFormTarget)
    this._syncHeaderSave()

    // Hide transcript echo (not relevant for manual entry)
    const echo = this.confirmCardTarget.querySelector('[data-field="transcript"]')
    if (echo) echo.classList.add("hidden")

    // Swap button sets: hide AI edit buttons, show manual save buttons
    if (this.hasEditButtonsTarget) this.editButtonsTarget.classList.add("hidden")
    if (this.hasManualButtonsTarget) this.manualButtonsTarget.classList.remove("hidden")
    if (this.hasManualErrorTarget) this.manualErrorTarget.classList.add("hidden")

    this._refreshSaveButtonsState()
  }

  cancelManual() {
    this._isManualEntry = false

    // Restore normal state
    this._hide(this.confirmCardTarget)
    this._hide(this.editFormTarget)
    this._show(this.inputAreaTarget)
    this._syncHeaderSave()

    // Unhide transcript echo
    const echo = this.confirmCardTarget.querySelector('[data-field="transcript"]')
    if (echo) echo.classList.remove("hidden")

    // Restore button sets
    if (this.hasEditButtonsTarget) this.editButtonsTarget.classList.remove("hidden")
    if (this.hasManualButtonsTarget) this.manualButtonsTarget.classList.add("hidden")

    // Restore display area visibility for future AI flow
    if (this.hasDisplayAreaTarget) this.displayAreaTarget.classList.remove("hidden")
  }

  saveManualExpense() {
    const name = this.editNameTarget.value.trim()
    const amount = this.editAmountTarget.value.trim()

    // Validate required fields — show flash-alert-style error at top
    if (!name || !amount || parseFloat(amount) <= 0) {
      this._showManualError(this._t("save_failed"))
      if (!name) this.editNameTarget.focus()
      else if (!amount || parseFloat(amount) <= 0) this.editAmountTarget.focus()
      return
    }

    this._hideManualError()

    const startDate = this.hasEditStartDateTarget ? this.editStartDateTarget.value : null

    const expense = {
      name,
      amount,
      currency: this._parsedExpense?.currency || this.defaultCurrencyValue,
      frequency: this._editFrequency || "monthly",
      recurring: this._editRecurring,
      category: this._editCategory || "other",
      icon: this._editCategory || "other",
      expense_type: this._parsedExpense?.expense_type || "consuming",
      start_date: startDate || null
    }

    this._isManualEntry = false
    this._closeAndSubmit(expense)
  }

  saveExpense() {
    if (!this._parsedExpense || !this.hasSaveUrlValue || !this.saveUrlValue) return
    if (!this._isParsedExpenseValid()) return
    this._closeAndSubmit(this._parsedExpense)
  }

  // Whether the currently-parsed expense satisfies the server-side
  // validations (amount > 0, name present). Drives the disabled state
  // of every save/confirm button in the modal so the user cannot fire
  // a submission that would 422.
  _isParsedExpenseValid() {
    if (!this._parsedExpense) return false
    const amount = parseFloat(this._parsedExpense.amount)
    const name   = (this._parsedExpense.name || "").trim()
    return !Number.isNaN(amount) && amount > 0 && name.length > 0
  }

  _isEditFormValid() {
    const amount = this.hasEditAmountTarget ? parseFloat(this.editAmountTarget.value) : NaN
    const name   = this.hasEditNameTarget ? this.editNameTarget.value.trim() : ""
    return !Number.isNaN(amount) && amount > 0 && name.length > 0
  }

  // Reflect validity on every save/confirm/apply button. Called after
  // AI parse, after edit-form input, and after applyEdit().
  _refreshSaveButtonsState() {
    const parsedValid = this._isParsedExpenseValid()
    const editValid   = this._isEditFormValid()

    if (this.hasConfirmButtonTarget) this._setButtonEnabled(this.confirmButtonTarget, parsedValid)
    if (this.hasApplyEditButtonTarget) this._setButtonEnabled(this.applyEditButtonTarget, editValid)
    if (this.hasManualSaveButtonTarget) this._setButtonEnabled(this.manualSaveButtonTarget, editValid)
  }

  _setButtonEnabled(btn, enabled) {
    btn.disabled = !enabled
    btn.classList.toggle("opacity-40", !enabled)
    btn.classList.toggle("pointer-events-none", !enabled)
  }

  // Close the modal, then submit expense data via a hidden Turbo form.
  // Using requestSubmit() instead of fetch() ensures the response goes through
  // Turbo's native adapter — critical for iOS Hotwire Native where fetch()
  // bypasses the native pipeline and turbo_stream responses are not processed.
  _closeAndSubmit(expense) {
    this.close()

    // Force-hide modal after animation completes
    setTimeout(() => {
      if (this.hasSheetTarget) this.sheetTarget.classList.add("hidden")
      if (this.hasBackdropTarget) this.backdropTarget.classList.add("hidden")
    }, 350)

    this._submitExpenseForm(expense)
  }

  _submitExpenseForm(expense) {
    if (!this.saveUrlValue) return

    const form = document.createElement("form")
    form.method = "POST"
    form.action = this.saveUrlValue
    form.style.display = "none"

    // CSRF token
    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    if (csrf) {
      const csrfInput = document.createElement("input")
      csrfInput.type = "hidden"
      csrfInput.name = "authenticity_token"
      csrfInput.value = csrf
      form.appendChild(csrfInput)
    }

    // Expense fields as expense[key] for Rails strong parameters
    for (const [key, value] of Object.entries(expense)) {
      if (value != null) {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = `expense[${key}]`
        input.value = String(value)
        form.appendChild(input)
      }
    }

    document.body.appendChild(form)

    // Clean up form after Turbo processes the response
    const cleanup = () => { if (form.parentNode) form.remove() }
    form.addEventListener("turbo:submit-end", cleanup, { once: true })
    setTimeout(cleanup, 10000) // safety net

    form.requestSubmit()
  }

  _populateConfirmCard(data, transcript) {
    if (transcript !== undefined) this._lastTranscript = transcript

    const card = this.confirmCardTarget
    const $ = (sel) => card.querySelector(`[data-field="${sel}"]`)

    // Transcript echo
    const displayTranscript = transcript !== undefined ? transcript : this._lastTranscript
    const echo = $("transcript")
    echo.textContent = displayTranscript ? `"${displayTranscript}"` : ""
    echo.classList.toggle("hidden", !displayTranscript)

    // Icon — prefer the AI-picked brand icon when present, otherwise fall back to the category icon.
    const iconBox = $("icon-box")
    const iconSpan = $("icon")
    if (data.brand_icon && data.brand_icon_svg) {
      const inner = data.brand_icon_svg.replace(/<svg\b/, '<svg width="22" height="22" fill="#FFFFFF" style="fill:#FFFFFF"')
      iconSpan.innerHTML = inner
      iconBox.style.background = `#${data.brand_icon_color || "94A3B8"}`
      iconBox.style.removeProperty("--c-light")
      iconBox.style.removeProperty("--c-dark")
    } else {
      const rawIcon = data.icon
      const icon = (rawIcon && rawIcon !== "other") ? rawIcon : (data.category || "other")
      iconSpan.innerHTML = iconSvgHtml(icon, 22)
      iconBox.style.removeProperty("background")
      applyIconTileStyle(iconBox, icon)
    }

    // Name
    $("name").textContent = data.name || "\u2014"

    // Frequency badge
    const freq = data.frequency || "monthly"
    const recurring = data.recurring !== false
    const freqEl = $("frequency")
    const freqLabels = this.i18nValue.frequency || {}
    if (recurring) {
      freqEl.textContent = freqLabels[freq] || freq
      const validKinds = ["daily", "weekly", "monthly", "yearly"]
      const kind = validKinds.includes(freq) ? freq : "monthly"
      freqEl.className = `chip-freq chip-freq--md chip-freq-${kind}`
    } else {
      freqEl.textContent = this._t("one_time_label") || "One-time"
      freqEl.className = "chip-freq chip-freq--md chip-freq-onetime"
    }

    // Category
    const catEl = $("category")
    if (data.category) {
      catEl.textContent = `\u00B7 ${categoryLabel(data.category)}`
      catEl.classList.remove("hidden")
    } else {
      catEl.classList.add("hidden")
    }

    // Amount — whole and cents are separate nodes so the cents can be styled
    // back. A trailing ".00" is dropped entirely rather than rendered faint.
    const amt = parseFloat(data.amount) || 0
    const [whole, cents] = amt.toFixed(2).split(".")
    $("amount").textContent = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    const centsEl = $("amount-cents")
    if (centsEl) centsEl.textContent = cents === "00" ? "" : `.${cents}`

    // Currency (translated symbol)
    const currencyCode = data.currency || "USD"
    const currencies = this.i18nValue.currencies || {}
    $("currency").textContent = currencies[currencyCode] || currencyCode

    // Source amount (shown when the server converted the currency). The ISO
    // code is used rather than the symbol — "$" belongs to four currencies.
    const origWrap = $("original-wrapper")
    if (origWrap) {
      const origCurrency = data.original_currency
      const origAmount = parseFloat(data.original_amount) || 0
      if (origCurrency && origCurrency !== currencyCode && origAmount > 0) {
        $("original-code").textContent = origCurrency
        $("original-amount").textContent = origAmount.toFixed(2)
        origWrap.classList.remove("hidden")
      } else {
        origWrap.classList.add("hidden")
      }
    }

    // Renewal date
    const renewalEl = $("renewal-date")
    const renewalWrapper = $("renewal-wrapper")
    if (renewalEl) {
      const renewalDate = recurring
        ? this._computeNextRenewal(data.start_date, freq)
        : null
      if (renewalDate) {
        const tmpl = this._t("renews_on") || "Renews: %{date}"
        renewalEl.textContent = tmpl.replace("%{date}", this._formatShortDate(renewalDate))
        if (renewalWrapper) {
          renewalWrapper.classList.remove("hidden")
          renewalWrapper.classList.add("renewal-pulse")
        }
      } else {
        renewalEl.textContent = ""
        if (renewalWrapper) renewalWrapper.classList.add("hidden")
      }
    }
  }

  _monthlyEstimate(amount, frequency, recurring) {
    if (!recurring) return null  // one-time — no projection
    switch (frequency) {
      case "daily":   return amount * 30
      case "weekly":  return amount * 4.33
      case "monthly": return amount
      case "yearly":  return amount / 12
      default:        return null
    }
  }

  // ── Inline edit ───────────────────────────────────────────────────────

  editExpense() {
    if (!this._parsedExpense) return

    // Navigate to the dedicated new expense page with AI data prefilled
    if (this.hasNewUrlValue && this.newUrlValue) {
      this.openPrefilled(this._parsedExpense)
      return
    }

    // Fallback: inline edit form
    this._populateEditForm(this._parsedExpense)
    this._hide(this.displayAreaTarget)
    this._show(this.editFormTarget)
  }

  cancelEdit() {
    this._hide(this.editFormTarget)
    this._show(this.displayAreaTarget)
  }

  applyEdit() {
    this._applyNewExpenseEdit()
  }

  _applyNewExpenseEdit() {
    const name = this.editNameTarget.value.trim()
    const amount = this.editAmountTarget.value.trim()

    if (name) this._parsedExpense.name = name
    if (amount) this._parsedExpense.amount = amount
    this._parsedExpense.frequency = this._editFrequency || "monthly"
    this._parsedExpense.recurring = this._editRecurring
    this._parsedExpense.category = this._editCategory || "other"
    this._parsedExpense.icon = this._editCategory || "other"
    // Preserve the parsed type (e.g. income from "received my salary") —
    // editing details must not silently convert it back to an expense.
    this._parsedExpense.expense_type = this._parsedExpense.expense_type || "consuming"
    if (this.hasEditStartDateTarget && this.editStartDateTarget.value) {
      this._parsedExpense.start_date = this.editStartDateTarget.value
    }

    // Re-populate the display card with updated values
    this._populateConfirmCard(this._parsedExpense)

    // Swap edit → display
    this._hide(this.editFormTarget)
    this._show(this.displayAreaTarget)

    this._refreshSaveButtonsState()
  }

  // Re-evaluate save-button state whenever the user types in the
  // amount or name fields of the inline edit form.
  onEditFieldInput() {
    this._refreshSaveButtonsState()
  }

  // ── Shared edit form population ──────────────────────────────────────

  _populateEditForm(data) {
    this.editNameTarget.value = data.name || ""
    this.editAmountTarget.value = data.amount || "0"
    const currencyCode = data.currency || "USD"
    const currencies = this.i18nValue.currencies || {}
    this.editCurrencyTarget.textContent = currencies[currencyCode] || currencyCode

    this._editFrequency = data.frequency || "monthly"
    this._editRecurring = data.recurring !== false
    this._editCategory = data.category || "other"

    // Start date
    if (this.hasEditStartDateTarget) {
      this.editStartDateTarget.value = data.start_date || ""
    }
    this._syncStartDateDisplay()

    this._syncFrequencyPills()
    this._syncRecurringToggle(this._editRecurring)
    this._syncCategoryTrigger()
    this._syncStartDateSection()
    this._updateRenewalPreview()
  }

  // ── Sliding pill core ──────────────────────────────────────────────────

  _slideIndicator(slider, buttons, activeIndex) {
    if (!slider) return
    const isRTL = document.documentElement.dir === "rtl"
    const pct = activeIndex * 100
    slider.style.transform = `translateX(${isRTL ? -pct : pct}%)`
  }

  // ── Frequency — sliding segmented control ────────────────────────────

  selectFrequency(event) {
    this._editFrequency = event.currentTarget.dataset.freq
    this._syncFrequencyPills()
    this._updateRenewalPreview()
  }

  _syncFrequencyPills() {
    const freqs = ["daily", "weekly", "monthly", "yearly"]
    const activeIdx = freqs.indexOf(this._editFrequency)
    const isRecurring = this._editRecurring
    const slider = this.hasFreqSliderTarget ? this.freqSliderTarget : null

    // Slide the indicator
    this._slideIndicator(slider, this.frequencyPillTargets, Math.max(0, activeIdx))

    // When not recurring: hide slider, dim all pills
    if (slider) {
      slider.style.opacity = isRecurring ? "1" : "0"
    }
    if (this.hasFreqTrackTarget) {
      this.freqTrackTarget.classList.toggle("pointer-events-none", !isRecurring)
    }

    this.frequencyPillTargets.forEach(pill => {
      const active = pill.dataset.freq === this._editFrequency
      pill.classList.remove("text-slate-900", "text-slate-400", "text-slate-300")
      if (!isRecurring) {
        pill.classList.add("text-slate-300")
      } else {
        pill.classList.add(active ? "text-slate-900" : "text-slate-400")
      }
    })
  }

  // ── Recurring toggle ─────────────────────────────────────────────────

  toggleRecurring() {
    this._editRecurring = !this._editRecurring
    this._syncRecurringToggle(this._editRecurring)
  }

  _syncRecurringToggle(isRecurring) {
    if (!this.hasRecurringToggleTarget) return
    const isRTL = document.documentElement.dir === "rtl"

    this.recurringToggleTarget.classList.toggle("bg-blue-600", isRecurring)
    this.recurringToggleTarget.classList.toggle("bg-slate-300", !isRecurring)

    if (this.hasRecurringKnobTarget) {
      const onTranslate = isRTL ? "-translate-x-[22px]" : "translate-x-[22px]"
      const offTranslate = isRTL ? "-translate-x-0.5" : "translate-x-0.5"
      // Clear previous translate classes
      this.recurringKnobTarget.className = "inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200"
      this.recurringKnobTarget.classList.add(isRecurring ? onTranslate : offTranslate)
    }

    // Sync frequency pills to reflect recurring state
    this._syncFrequencyPills()
    this._syncStartDateSection()
    this._updateRenewalPreview()
  }

  _syncStartDateSection() {
    if (!this.hasStartDateSectionTarget) return
    if (this._editRecurring) {
      this.startDateSectionTarget.classList.remove("hidden")
    } else {
      this.startDateSectionTarget.classList.add("hidden")
    }
  }

  // Called when the start date input changes
  onStartDateChange() {
    this._syncStartDateDisplay()
    this._updateRenewalPreview()
  }

  _syncStartDateDisplay() {
    if (!this.hasEditStartDateDisplayTarget) return
    const val = this.hasEditStartDateTarget ? this.editStartDateTarget.value : ""
    this.editStartDateDisplayTarget.textContent = val ? this._formatShortDate(val) : "—"
  }

  // ── Category picker ──────────────────────────────────────────────────

  openCategoryPicker() {
    if (!this.hasCategoryPickerOverlayTarget) return
    const overlay = this.categoryPickerOverlayTarget
    overlay.classList.remove("hidden", "category-picker-exit")
    overlay.classList.add("category-picker-enter")
    this._syncCategoryGrid()
  }

  closeCategoryPicker() {
    if (!this.hasCategoryPickerOverlayTarget) return
    const overlay = this.categoryPickerOverlayTarget
    overlay.classList.remove("category-picker-enter")
    overlay.classList.add("category-picker-exit")
    const handler = () => {
      overlay.classList.add("hidden")
      overlay.classList.remove("category-picker-exit")
      overlay.removeEventListener("animationend", handler)
    }
    overlay.addEventListener("animationend", handler)
  }

  selectCategoryFromPicker(event) {
    this._editCategory = event.currentTarget.dataset.cat
    this._syncCategoryTrigger()
    this._syncCategoryGrid()
    // Auto-close after a brief flash so the ring highlight is visible
    setTimeout(() => this.closeCategoryPicker(), 180)
  }

  _syncCategoryTrigger() {
    const cat = this._editCategory || "other"
    if (this.hasCategoryTriggerIconTarget) {
      this.categoryTriggerIconTarget.innerHTML = iconSvgHtml(cat, 28)
      applyIconTileStyle(this.categoryTriggerIconTarget, cat)
    }
    if (this.hasCategoryTriggerLabelTarget) {
      this.categoryTriggerLabelTarget.textContent = categoryLabel(cat)
    }
  }

  _syncCategoryGrid() {
    this.categoryGridItemTargets.forEach(item => {
      const active = item.dataset.cat === this._editCategory
      item.classList.toggle("ring-2", active)
      item.classList.toggle("ring-blue-500", active)
      item.classList.toggle("ring-offset-2", active)
      item.classList.toggle("bg-blue-50/50", active)
    })
  }

  _handleSaveError(errors) {
    const msg = errors?.join(", ") || this._t("save_failed")

    if (this._isManualEntry) {
      this._showManualError(msg)
      return
    }

    if (this.hasStatusTarget) this.statusTarget.textContent = msg
    this._hide(this.confirmCardTarget)
    this._show(this.inputAreaTarget)
    this._syncHeaderSave()
  }

  // ── Native bridge callbacks ────────────────────────────────────────────────

  // "ready" — audio engine confirmed live
  _handleNativeReady(_event) {
    this._setRecordingState(true)
    this._startTimer()
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this._t("listening")
    }
  }

  // "partial" — live word-by-word stream while speaking.
  // iOS SFSpeechRecognizer processes in segments: after a pause it starts a fresh
  // partial stream for the next segment. We detect this by checking if the new
  // partial is significantly shorter than the last one — if so, the previous
  // segment completed and we commit it before appending the new partial.
  _handleNativePartial({ detail: { transcript } }) {
    // Late partial from a recording the user already cleared — ignore it.
    if (this._discardPendingResult) return

    this._setRecordingState(true)

    if (this._lastPartial && transcript.length < this._lastPartial.length * 0.5) {
      // New segment started — commit the previous one
      this._committedTranscript = [this._committedTranscript, this._lastPartial]
        .filter(Boolean).join(" ").trim()
    }

    this._lastPartial = transcript

    const full = [this._committedTranscript, transcript]
      .filter(Boolean).join(" ").trim()

    this.transcriptTarget.value = full.slice(0, TRANSCRIPT_MAX_CHARS)
    this._updateSubmitState()
    this.statusTarget.textContent = this._t("listening")
  }

  // "transcript" — final result when recording stops.
  // Combine any committed segments with the final segment text.
  _handleNativeTranscript({ detail: { transcript, error } }) {
    this._nativeRecording = false
    this._setRecordingState(false)

    // Final result of a recording the user cleared mid-flight — swallow it
    // (including errors) and keep the freshly-reset state.
    if (this._discardPendingResult) {
      this._discardPendingResult = false
      return
    }

    if (error) {
      // Dictation disabled in iOS Settings: on-device recognition can never run,
      // so retrying is pointless. Don't show a tiny "no speech" status — put up a
      // prominent, persistent alert + confused mascot telling the user exactly
      // how to fix it. (Native bridge sends code "dictation_disabled".)
      if (error === "dictation_disabled") {
        this._showConfusedMascot()
        this._showDictationAlert(this._t("dictation_disabled"))
        if (this.hasStatusTarget) this.statusTarget.textContent = this._t("dictation_off")
        return
      }
      // Keyed by the stable error CODES the native bridge sends (see
      // VoiceBridgeComponent.sendError), not by English phrases — the bridge
      // never sends prose, so phrase keys never matched and leaked raw codes.
      const nativeErrorMap = {
        no_speech:            this._t("no_speech"),
        mic_denied:           this._t("mic_not_granted"),
        speech_denied:        this._t("mic_not_granted"),
        dictation_disabled:   this._t("dictation_disabled"),
        language_unavailable: this._t("speech_not_supported"),
        audio_session_failed: this._t("generic_error", { error }),
        audio_engine_failed:  this._t("generic_error", { error })
      }
      this.statusTarget.textContent = nativeErrorMap[error] || this._t("generic_error", { error })
      return
    }
    if (transcript) {
      const full = [this._committedTranscript, transcript]
        .filter(Boolean).join(" ").trim()
      this.transcriptTarget.value = full.slice(0, TRANSCRIPT_MAX_CHARS)
      this._committedTranscript = ""
      this._lastPartial = ""
      this.statusTarget.textContent = this._t("done_review")
      this._updateSubmitState()
    }
  }

  // ── Web Speech API fallback ────────────────────────────────────────────────

  _startListening() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR) {
      if (this.hasStatusTarget) {
        this.statusTarget.textContent = this._t("speech_not_supported")
      }
      return
    }

    this._recognition = new SR()
    this._discardPendingResult = false
    this._recognition.lang = speechLocale()
    this._recognition.continuous = false
    this._recognition.interimResults = true

    this._recognition.onstart = () => {
      this._isListening = true
      this._setRecordingState(true)
      this._startTimer()
      this.statusTarget.textContent = this._t("listening")
      this.transcriptTarget.value = ""
      this._updateSubmitState()
    }

    this._recognition.onresult = (event) => {
      if (this._discardPendingResult) return
      let interim = "", final = ""
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript
        event.results[i].isFinal ? (final += text) : (interim += text)
      }
      this.transcriptTarget.value = (final || interim).slice(0, TRANSCRIPT_MAX_CHARS)
      this._updateSubmitState()
    }

    this._recognition.onend = () => {
      this._isListening = false
      this._setRecordingState(false)
      // Recording was cleared mid-flight — keep the reset status, don't
      // overwrite it with "nothing heard".
      if (this._discardPendingResult) {
        this._discardPendingResult = false
        return
      }
      if (this.transcriptTarget.value.trim()) {
        this.statusTarget.textContent = this._t("done_review")
        this._updateSubmitState()
      } else {
        this.statusTarget.textContent = this._t("nothing_heard")
      }
    }

    this._recognition.onerror = (event) => {
      this._isListening = false
      this._setRecordingState(false)
      if (event.error === "not-allowed") this._showConfusedMascot()
      const msgs = {
        "not-allowed": this._t("mic_not_granted"),
        "no-speech":   this._t("no_speech"),
        "network":     this._t("network_error")
      }
      this.statusTarget.textContent = msgs[event.error] || this._t("generic_error", { error: event.error })
    }

    this._recognition.start()
  }

  _stopListening() {
    if (this._recognition) this._recognition.stop()
    this._isListening = false
    this._setRecordingState(false)
  }

  // ── Submit state ───────────────────────────────────────────────────────────

  _updateSubmitState() {
    if (this.transcriptTarget.value.length > TRANSCRIPT_MAX_CHARS) {
      this.transcriptTarget.value = this.transcriptTarget.value.slice(0, TRANSCRIPT_MAX_CHARS)
    }

    const rawLen = this.transcriptTarget.value.length
    const len = this.transcriptTarget.value.trim().length
    const isReady = len >= this.minCharsValue

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !isReady
    }

    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.disabled = rawLen === 0
    }

    if (this.hasCharCountTarget) {
      this.charCountTarget.textContent = `${rawLen} / ${TRANSCRIPT_MAX_CHARS}`
      this.charCountTarget.classList.toggle("text-red-500", rawLen >= TRANSCRIPT_MAX_CHARS)
    }

    this._syncHeaderSave()
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  // Look up a translation key from the i18n Stimulus value passed by Rails.
  // Supports simple %{var} interpolation for keys like generic_error.
  _t(key, vars = {}) {
    let text = this.i18nValue[key] || key
    for (const [k, v] of Object.entries(vars)) {
      text = text.replace(`%{${k}}`, v)
    }
    return text
  }

  // Returns the bridge--bottom-nav controller if connected (native iOS app).
  get _bottomNavBridge() {
    const el = this.element.querySelector("[data-controller~='bridge--bottom-nav']")
      || document.querySelector("[data-controller~='bridge--bottom-nav']")
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(el, "bridge--bottom-nav") || null
  }

  // Returns the bridge--expense-dock controller if connected (native iOS app).
  // It lives on the sheet, which is where expense-deck lives too.
  get _expenseDockBridge() {
    if (!this.hasSheetTarget) return null
    return this.application.getControllerForElementAndIdentifier(
      this.sheetTarget, "bridge--expense-dock"
    ) || null
  }

  // Returns the bridge--voice controller if it is connected (i.e. we are inside
  // the native iOS app). Returns null in a plain browser — no UA sniffing needed.
  get _bridgeController() {
    if (!this.hasMicButtonTarget) return null
    return this.application.getControllerForElementAndIdentifier(
      this.micButtonTarget, "bridge--voice"
    ) || null
  }

  // ── Manual error flash alert ────────────────────────────────────────────

  dismissManualError() {
    this._hideManualError()
  }

  _showManualError(msg) {
    if (!this.hasManualErrorTarget) return
    if (this.hasManualErrorTextTarget) this.manualErrorTextTarget.textContent = msg
    this.manualErrorTarget.classList.remove("hidden")
    // Re-trigger entrance animation
    this.manualErrorTarget.classList.remove("voice-fade-in")
    void this.manualErrorTarget.offsetWidth
    this.manualErrorTarget.classList.add("voice-fade-in")
  }

  _hideManualError() {
    if (!this.hasManualErrorTarget) return
    this.manualErrorTarget.classList.add("hidden")
  }

  // ── Dictation-disabled alert ─────────────────────────────────────────────

  dismissDictationAlert() {
    this._hideDictationAlert()
  }

  _showDictationAlert(msg) {
    if (!this.hasDictationAlertTarget) return
    if (this.hasDictationAlertTextTarget) this.dictationAlertTextTarget.textContent = msg
    // Every banner starts without a Retry; _showInputError puts one back when
    // the failure it is reporting has something to send again. Otherwise a
    // dictation warning raised after a failed scan would inherit that scan's
    // Retry and resend a receipt nobody asked about.
    if (this.hasRetryScanTarget) this.retryScanTarget.classList.add("hidden")
    this.dictationAlertTarget.classList.remove("hidden")
    // Re-trigger entrance animation
    this.dictationAlertTarget.classList.remove("voice-fade-in")
    void this.dictationAlertTarget.offsetWidth
    this.dictationAlertTarget.classList.add("voice-fade-in")
  }

  _hideDictationAlert() {
    if (!this.hasDictationAlertTarget) return
    this.dictationAlertTarget.classList.add("hidden")
    // The Retry rides inside the banner, so it leaves with it.
    if (this.hasRetryScanTarget) this.retryScanTarget.classList.add("hidden")
  }

  // Show an element with a fade+slide-in animation.
  _show(el) {
    if (!el) return
    this._beginCardResize()
    el.classList.remove("hidden")
    // Re-trigger animation even if the class was already there
    el.classList.remove("voice-fade-in")
    void el.offsetWidth // force reflow
    el.classList.add("voice-fade-in")
    if (this._isInputArea(el)) this._setInputVisible(true)
  }

  // Hide instantly — the incoming element's fade-in masks the abrupt removal.
  _hide(el) {
    if (!el) return
    this._beginCardResize()
    el.classList.remove("voice-fade-in")
    el.classList.add("hidden")
    if (this._isInputArea(el)) this._setInputVisible(false)
  }

  _isInputArea(el) {
    return this.hasInputAreaTarget && el === this.inputAreaTarget
  }

  // ── Card resize ────────────────────────────────────────────────────────────
  //
  // Swapping the input area for the loading skeleton, or the skeleton for the
  // confirm card, changes the card's height by a couple of hundred pixels in
  // one frame. The stack is anchored to the bottom, so that lands as the top
  // edge jumping rather than moving. Measure before and after, then play the
  // difference back as a transition.
  //
  // Every swap is a burst of _hide/_show calls in one synchronous block, so the
  // first of them opens the window and the rest are no-ops; the measurement is
  // taken in the rAF that follows, which still runs before the browser paints
  // the jumped layout.

  _beginCardResize() {
    if (!this.hasCardTarget || !this._sheetOpen) return
    // A closed sheet has nothing to show, and the first call of the tick is the
    // only one that saw the height before any of the swaps landed.
    if (this._cardResizePending) return
    this._cardResizePending = true
    // Mid-flight this reads the height the card is currently animating through,
    // so an interrupted resize continues from where it actually is.
    this._cardResizeFrom = this.cardTarget.getBoundingClientRect().height
    requestAnimationFrame(() => this._commitCardResize())
  }

  _commitCardResize() {
    this._cardResizePending = false
    if (!this.hasCardTarget) return

    const card = this.cardTarget
    const from = this._cardResizeFrom

    // Drop any inline height first, or the "after" measurement just reads the
    // value left behind by the previous resize.
    card.style.transition = "none"
    card.style.height = ""
    const to = card.getBoundingClientRect().height

    const worthAnimating = from && to && Math.abs(to - from) >= 1
    if (!worthAnimating || this._prefersReducedMotion) {
      card.style.transition = ""
      card.style.overflow = ""
      return
    }

    // Clipped for the duration: the outgoing content is still laid out at its
    // full size for the first frames and would otherwise spill past the card's
    // rounded edge.
    card.style.overflow = "hidden"
    card.style.height = `${from}px`
    void card.offsetHeight // commit the start height before the transition
    card.style.transition = `height ${CARD_RESIZE_MS}ms ${CARD_RESIZE_EASING}`
    card.style.height = `${to}px`

    this._endCardResize(card)
  }

  // Hands the card back to its content once it has arrived. The timeout is the
  // safety net for a transitionend that never fires — an interrupted resize, or
  // the sheet closing mid-flight.
  _endCardResize(card) {
    clearTimeout(this._cardResizeTimer)
    if (this._cardResizeSettle) {
      card.removeEventListener("transitionend", this._cardResizeSettle)
    }

    const settle = (event) => {
      if (event && event.target !== card) return
      card.removeEventListener("transitionend", settle)
      clearTimeout(this._cardResizeTimer)
      this._cardResizeSettle = null
      card.style.transition = ""
      card.style.height = ""
      card.style.overflow = ""
    }

    this._cardResizeSettle = settle
    card.addEventListener("transitionend", settle)
    this._cardResizeTimer = setTimeout(settle, CARD_RESIZE_MS + 80)
  }

  get _prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  _setInputVisible(visible) {
    this._inputVisible = visible
    this._syncDock()
  }

  // The ways-in dock is the sheet's second glass pane, a sibling of the card
  // rather than a child of the input area — the only way its blur has the
  // dashboard to chew on instead of the card's own fill. The cost of that is
  // this: it stops riding the input area's hidden class, so one function owns
  // the whole answer rather than a dozen call sites each toggling a class.
  _syncDock() {
    // Whether the sheet has a dock at all is the input area's business: loading,
    // the confirm card and the consent overlay each replace it.
    const inPlace = this._inputVisible
    const bridge = this._expenseDockBridge

    if (!bridge) {
      // Plain browser. The web capsule is the real one and rides the sheet, so
      // it follows the input area directly: it is content, and content that no
      // longer applies should not hold a place open.
      this._collapseWebDock(!inPlace)
      return
    }

    // Native app. ExpenseDockComponent draws the capsule, pinned to the window
    // rather than riding the sheet, so it needs the three things the web one
    // gets for free: stay away while the sheet is closed, step out once the
    // sheet is genuinely being dragged, and clear the keyboard.
    const nativeVisible = inPlace &&
      this._sheetOpen &&
      !this._sheetDragging &&
      !this._typing

    // The web dock keeps its place in the layout at visibility:hidden, purely to
    // reserve the capsule's 58px — and it keeps it for the whole time the sheet
    // is open, whatever the capsule is doing. Giving the space back is what made
    // Save slam: the stack is anchored to the bottom, so the card drops by the
    // dock's height plus the stack's gap in a single frame. Held open, the
    // card's bottom edge never moves at all and only its top edge follows the
    // content, which is the whole of the transition worth having.
    this._collapseWebDock(false)

    if (this._dockShown === nativeVisible) return
    this._dockShown = nativeVisible
    nativeVisible ? bridge.show() : bridge.hide()
  }

  _collapseWebDock(collapsed) {
    if (this.hasDockTarget) this.dockTarget.classList.toggle("hidden", collapsed)
  }

  // A drag only counts once the sheet is actually translating — an upward or
  // stationary gesture commits without moving the card, and pulling the dock
  // away from a card that has not moved just leaves a hole.
  _setSheetDragging(dragging) {
    if (this._sheetDragging === dragging) return
    this._sheetDragging = dragging
    this._syncDock()
  }

  // The transcript belongs to Speak and Type; focusing it raises the keyboard
  // over the window-pinned native dock. Nothing else in the app watches the
  // keyboard, so this is the signal.
  onTranscriptFocus() {
    this._typing = true
    this._syncDock()
  }

  onTranscriptBlur() {
    this._typing = false
    this._syncDock()
  }

  _setRecordingState(recording) {
    if (!this.hasMicButtonTarget) return

    // Swap mascot images
    if (this.hasMascotOffTarget) this.mascotOffTarget.classList.toggle("hidden", recording)
    if (this.hasMascotOnTarget) this.mascotOnTarget.classList.toggle("hidden", !recording)
    if (this.hasMascotConfusedTarget) this.mascotConfusedTarget.classList.add("hidden")

    // Toggle ripple rings
    if (this.hasRippleRingsTarget) this.rippleRingsTarget.classList.toggle("hidden", !recording)

    // Stop countdown timer when recording ends
    if (!recording) this._stopTimer()
  }

  // ── Recording countdown timer ─────────────────────────────────────────────

  _startTimer() {
    this._stopTimer()
    this._remainingSeconds = RECORDING_DURATION_SECS

    if (this.hasTimerTextTarget) {
      this.timerTextTarget.style.display = ""
      this._updateTimerDisplay()
    }

    this._timerInterval = setInterval(() => {
      this._remainingSeconds -= 1
      this._updateTimerDisplay()

      if (this._remainingSeconds <= 0) {
        this._stopTimer()
        this._handleRecordingTimeout()
      }
    }, 1000)
  }

  // Called when the on-screen countdown reaches zero. We force-stop the
  // active recording (native bridge or Web Speech) so the status no longer
  // reads "Listening" after the period is over.
  _handleRecordingTimeout() {
    const hadTranscript = !!this.transcriptTarget?.value?.trim()

    if (this._nativeRecording) {
      this._bridgeController?.stop()
      this._nativeRecording = false
      this._setRecordingState(false)
    } else if (this._isListening) {
      this._stopListening()
    } else {
      this._setRecordingState(false)
    }

    if (this.hasStatusTarget) {
      this.statusTarget.textContent = hadTranscript
        ? this._t("done_review")
        : this._t("tap_to_start")
    }
  }

  _updateTimerDisplay() {
    if (!this.hasTimerTextTarget) return

    this.timerTextTarget.textContent = `${this._remainingSeconds}${timerUnit()}`

    if (this._remainingSeconds <= 3) {
      this.timerTextTarget.classList.remove("text-blue-400")
      this.timerTextTarget.classList.add("text-warning")
    } else {
      this.timerTextTarget.classList.remove("text-warning")
      this.timerTextTarget.classList.add("text-blue-400")
    }
  }

  _stopTimer() {
    if (this._timerInterval) {
      clearInterval(this._timerInterval)
      this._timerInterval = null
    }
    if (this.hasTimerTextTarget) {
      this.timerTextTarget.style.display = "none"
    }
  }

  _showConfusedMascot() {
    if (this.hasMascotOffTarget) this.mascotOffTarget.classList.add("hidden")
    if (this.hasMascotOnTarget) this.mascotOnTarget.classList.add("hidden")
    if (this.hasMascotConfusedTarget) this.mascotConfusedTarget.classList.remove("hidden")
  }

  // ── Date helpers ──────────────────────────────────────────────────────

  _formatShortDate(isoString) {
    if (!isoString) return ""
    const date = new Date(isoString + "T00:00:00")
    return date.toLocaleDateString(intlLocale(), { year: "numeric", month: "short", day: "numeric" })
  }

  _computeNextRenewal(startDateISO, frequency) {
    if (!startDateISO || !frequency) return null
    const start = new Date(startDateISO + "T00:00:00")
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const advance = (d) => {
      switch (frequency) {
        case "daily":   d.setDate(d.getDate() + 1); break
        case "weekly":  d.setDate(d.getDate() + 7); break
        case "monthly": d.setMonth(d.getMonth() + 1); break
        case "yearly":  d.setFullYear(d.getFullYear() + 1); break
        default:        d.setMonth(d.getMonth() + 1)
      }
    }

    // Start date = first occurrence. Renewal = next occurrence after start.
    // Advance at least once, then keep advancing until past today.
    let candidate = new Date(start)
    advance(candidate)
    while (candidate < today) { advance(candidate) }

    const y = candidate.getFullYear()
    const m = String(candidate.getMonth() + 1).padStart(2, '0')
    const d = String(candidate.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }

  _updateRenewalPreview() {
    if (!this.hasRenewalPreviewTarget) return
    const wrapper = this.hasRenewalPreviewWrapperTarget ? this.renewalPreviewWrapperTarget : null

    if (!this._editRecurring) {
      this.renewalPreviewTarget.textContent = ""
      if (wrapper) wrapper.classList.add("hidden")
      return
    }
    const startDate = this.hasEditStartDateTarget ? this.editStartDateTarget.value : null
    const renewal = this._computeNextRenewal(startDate, this._editFrequency)
    if (renewal) {
      const tmpl = this._t("renews_on") || "Renews: %{date}"
      this.renewalPreviewTarget.textContent = tmpl.replace("%{date}", this._formatShortDate(renewal))
      if (wrapper) {
        wrapper.classList.remove("hidden")
        wrapper.classList.remove("renewal-pulse")
        void wrapper.offsetWidth
        wrapper.classList.add("renewal-pulse")
      }
    } else {
      this.renewalPreviewTarget.textContent = ""
      if (wrapper) wrapper.classList.add("hidden")
    }
  }

  // Clones the server-rendered toast template (localized, shared/_toast) into
  // the flash container — same pattern as copy_controller. Deduped so rapid
  // taps on the mic don't stack pills.
  _showNoBudgetToast() {
    const container = document.getElementById("toast-notifications")
    if (!container || !this.hasNoBudgetToastTarget) return

    container.querySelectorAll("[data-no-budget-toast]").forEach(node => node.remove())

    const clone = this.noBudgetToastTarget.content.cloneNode(true)
    const wrapper = clone.firstElementChild
    if (wrapper) wrapper.setAttribute("data-no-budget-toast", "")
    container.appendChild(clone)
  }

  _resetSheet() {
    this._stopTimer()
    this._isListening = false
    this._nativeRecording = false
    this._discardPendingResult = false
    this._isManualEntry = false
    this._committedTranscript = ""
    this._lastPartial = ""
    this._parsedExpense = null
    this._scanBlob = null
    this._lastTranscript = ""
    this._editFrequency = null
    this._editRecurring = true
    this._editCategory = null
    this._setRecordingState(false)
    if (this.hasTranscriptTarget)   this.transcriptTarget.value = ""
    this._updateSubmitState()
    if (this.hasLoadingAreaTarget)  this.loadingAreaTarget.classList.add("hidden")
    if (this.hasConfirmCardTarget)  this.confirmCardTarget.classList.add("hidden")
    if (this.hasInputAreaTarget)    this.inputAreaTarget.classList.remove("hidden")
    this._setInputVisible(true)
    if (this.hasEditFormTarget)     this.editFormTarget.classList.add("hidden")
    if (this.hasDisplayAreaTarget)  this.displayAreaTarget.classList.remove("hidden")
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this._t("tap_to_start")
    }
    // Reset category picker overlay
    if (this.hasCategoryPickerOverlayTarget) {
      this.categoryPickerOverlayTarget.classList.add("hidden")
      this.categoryPickerOverlayTarget.classList.remove("category-picker-enter", "category-picker-exit")
    }
    // Reset date state
    if (this.hasEditStartDateTarget) this.editStartDateTarget.value = ""
    if (this.hasEditStartDateDisplayTarget) this.editStartDateDisplayTarget.textContent = "—"
    if (this.hasRenewalPreviewTarget) this.renewalPreviewTarget.textContent = ""
    if (this.hasRenewalPreviewWrapperTarget) this.renewalPreviewWrapperTarget.classList.add("hidden")
    if (this.hasStartDateSectionTarget) this.startDateSectionTarget.classList.add("hidden")
    // Reset slider positions
    if (this.hasFreqSliderTarget) this.freqSliderTarget.style.transform = ""
    // Reset manual mode button sets
    if (this.hasManualButtonsTarget) this.manualButtonsTarget.classList.add("hidden")
    if (this.hasEditButtonsTarget)   this.editButtonsTarget.classList.remove("hidden")
    if (this.hasManualErrorTarget)   this.manualErrorTarget.classList.add("hidden")
    // _scanBlob is gone, so a Retry left over from the last open would do
    // nothing at all. Take it off screen with the photo it belonged to.
    if (this.hasRetryScanTarget)     this.retryScanTarget.classList.add("hidden")
    // Unhide transcript echo
    const echo = this.hasConfirmCardTarget
      ? this.confirmCardTarget.querySelector('[data-field="transcript"]')
      : null
    if (echo) echo.classList.remove("hidden")
  }
}
